import { Project, ProjectNew } from '@dc-shared-web/hooks'
import { ChatCitationMap } from '@dc-shared-web/providers'

export type DocumentSearchResultStatus =
  | 'In Progress'
  | 'in-progress'
  | 'exporting'
  | 'error'
  | 'completed'

export type DocumentSearchResult = {
  uuid: string
  createdAt: Date
  createdAtDate: Date
  crunchedBy: string
  documentGuid: string
  fileName: string
  folderId: number | null
  folderUuid: string | null
  folderName: string
  isArchived: boolean
  isShared: boolean
  jobStatus: string // is there enum vals?
  projectId: number | null
  projectUuid: string | null
  projectName: string
  shareCount: number
  sharedWith: string
  status: DocumentSearchResultStatus
  teamId: number
  teamUUID: string
  teamName: string
  type: string // is there enum vals?
  user: string
  _shortenedUuid: string
  versions: string[]
  documentTypeName: string
}

export type SharedDocumentInfo = {
  id: number
  email: string
  shared_with: string
  shared_by: string
  revoked_by: string | null
  created_at: string
  accepted_at: string
  revoked_on: string
  is_expired: boolean
  is_accepted: boolean
  is_revoked: boolean
  shareLinkId: string
}

export type SharedDocumentInfoReponse = {
  count: number
  shares: { [key: string]: SharedDocumentInfo[] }
  _activeShareCount: number
}

export enum CrunchExtraTypeEnum {
  UserOccupation = 'user_occupation',
  DocType = 'doc_type',
  DocDescription = 'doc_description',
}

export interface CrunchExtrasItem {
  uuid: string
  crunch_uuid: string
  type: CrunchExtraTypeEnum
  value: string
  created_at: string
  created_by: string
  updated_at: string
  deleted_at?: string
  deleted_by?: string
}

// ** Document Crunch Data Start**
export type CrunchData = {
  document_guid: string
  kiraDocId: number
  checklist_status_data: {
    history: unknown[]
    status: {
      id: number
      name: string
    }
  }
  id: string
  shortUUID: string
  filename: string
  label: string
  typeId: number
  type: string
  crunchedBy: string
  crunchType: string
  createdOn: string
  team: string
  teamId: string | null
  teamUuid: string | null
  projectInfo: Project
  toc: TocData
  highlights: Provision[] | undefined
  subTypes: SubType
  surveyData: Survey
  availableSurveys: SurveyOption[]
  appliedChecklists?: ChecklistPreview[]
  appliedPlaybooks?: ChecklistPreview[]
  is_sbs_ready: boolean
  meta: {
    isHqPipeline: boolean
    isSuperAdmin: boolean
    isShared: boolean
    showChecklist: boolean
    showToc: boolean
    canRename: boolean
    showSbsPromo: boolean
    showRenoPromo: boolean
    showAIDisclaimer: boolean
    showCsSbsPromo: number
    areHighlightsProcessed: boolean
    isChecklist: boolean
    checklist_type: string
    isBothChecklistAndPlaybook: boolean
    isCheatSheet: boolean
    hasToc: boolean
    document_guid: string
    chatEnabled: boolean
    crunchExtras?: CrunchExtrasItem[]
  }
  jobs: {
    runGuid: string
    createdAt: string
    type: string
    updatedAt: string
  }[]
  documentTypeName: string
}

export type ChecklistPreview = {
  uuid: string
  checklist_template_uuid: string
  name: string
  aiGenerated: boolean
  hasGenAiResults: boolean
  hasSubquestions: boolean
  optionGroup: number
  createdAt: string
  questions: {
    total: number
    marked: number
    option1: number
    option2: number
    option3: number
  }
  type: string
}

export type TocData = {
  [key: string]: {
    [key: string]: TocDataObj
  }
}

export type TocDataObj = {
  label: string
  ranges: {
    page: string
    text: string
  }
}

export interface Provision {
  refId: string
  isPublic?: boolean
  pageIndex: number
  metadata: Metadata
  annotation?: unknown // Annotation;
  type: 'dc' | 'user'
  id?: number
  revision?: number
  created?: string
  blameable?: Blameable
  deleted?: boolean
  feedback?: string
  reviewed?: null | string
  reviewedBy?: string
  name?: string
  description?: string
  fid?: number
}

export interface Metadata {
  id?: string
  v?: number
  pdfObjectId?: number
  pageIndex?: number
  bbox?: number[]
  opacity?: number
  note?: string
  createdAt?: Date
  updatedAt?: Date
  isCommentThreadRoot?: boolean
  blendMode?: string
  type?: string
  rects?: number[][]
  color?: string
  customData: CustomData
}

export type CustomData = {
  id: string
  provision_text_fragment: string
  provision_fid: number
  provision_name: string
  provision_type: string
  provision_source?: string
}

export type Blameable = {
  email: string
  firstName: string
  lastName: string
}

export type SubType = {
  [key: string]: {
    name: string
    provisions: {
      [key: string]: Provision
    }
  }
}

export type Survey = {
  id: number | null
  uuid: string
  checklist_template_uuid?: string
  ai_generated: boolean | null
  qAnswers: { [key: string]: QAnswer }
  qOptionsFe: { [key: string]: QOption[] }
  qOptionsFeTopLevel: 'o1' | 'o2' | 'o3'
  sections: Section[]
  survey_name: string
  survey_description: string
}

export type QAnswer = {
  text?: string
  options?: string[] | null
  notes_and_comments?: string
  ai_generated?: boolean
  ai_answer?: QAIAnswer[]
  last_updated_by?: LastUpdatedData
}

export type QAIAnswer = {
  uuid: string
  summary: string
  last_updated_by: {
    name: string
    email: string
    updated_at: string //DTZ
  }
  citations?: {
    sources: ChatCitationMap
  }
}

type LastUpdatedData = {
  email: string
  last_update: string
  name: string
}

export type QOption = {
  id: number
  optionChoiceName: string
  label: string
}

export type Section = {
  position: number
  name: string
  title: string
  subheading: string
  ref_id: string
  questions: { [key: string]: Question }
}

export type Question = {
  is_complex: boolean
  name: string
  refId: string
  description: string
  notes_and_comments: boolean
  sub_questions: SubQuestion[]
  optionGroup: string | null
  policies_and_preferences: string | null
  pages: QuestionPages[]
  provisions: ItemData[]
  position: number
  subtext: string | null
  total_pages: number[]
  inputType: string
  required: boolean
  children: Question[]
}

export type SubQuestion = {
  description: string
  inputType: 'text' | 'textarea' | 'radio'
  name: string
  optionGroup: string | null
  refId: string
  required: boolean
}

export type QuestionPages = {
  all_pages: number[]
  label: string
  fid: number
}

type ItemData = {
  id: number
  name: string
  fid: number
  pages?: number[]
  description: string
  name_original: string
}

export type SurveyOption = {
  ai_enabled: boolean
  name: string
  ref_id: string
  side_by_side_ready: boolean
  type?: 'checklist' | 'playbook'
}
// ** Document Crunch Data End**

export type DocumentStatus = {
  uuid: string
  filename: string
  status: 'In Progress' | 'completed' | 'error'
  //This does not show up in the API response unless there is a checklist that is being generated
  genChecklistStatus?: 'In Progress' | 'completed' | 'error'
  genAIChecklists: Record<string, 'In Progress' | 'completed' | 'error'>
  genAIChecklistQuestions: Record<string, 'In Progress' | 'completed' | 'error'>
}

export interface ChecklistResponse {
  uuid: string
  crunch_uuid: string
  data: Survey
  created_at: string
  updated_at: string
  deleted_at: string | null
  created_by: string
  updated_by: string
  deleted_by: string
}

export interface OfficeDetails {
  uuid: string
  customerId: string
  officeName: string
  active: boolean
  internal: boolean
  trial: boolean
  partner: boolean
  isSideBySideReady: boolean
}

export interface DocumentSvcCrunches {
  id: number
  typeId: number
  createdById: number
  updatedById: number
  userId: number
  officeId: number
  office: OfficeDetails
  documentId: number
  label: string
  filename: string
  mimetype: string
  url: string
  uuid: string
  globalNote: string
  created: string
  updated: string
  deletedAt: string | null
  isPaid: boolean
  totalPages: number
  isArchived: boolean
  globalChecklistNote: string | null
  checklistId: string | null
  labelSearch: string
  projectInfoId: number | null
  highlightsProcessed: boolean
  hqPipeline: boolean
  tocData: TocDataSvcCrunches
  checklistData: ChecklistData
  versions: string[] | null
  folderId: number | null
  crunchType: string
  documentGuid: string
  parent: BreadcrumbData[]
  projectInfo: ProjectNew | null
  provisions: ProvisionGuidance[]
}

export interface BreadcrumbData {
  uuid: string
  name: string
  level: number
  type: string
  office_id: number
}

export interface TocDataSvcCrunches {
  name: string
  uuid: string
  label: string
  project: ProjectSvcCrunches
  version: number
  position: number
  sections: TocDateSection[]
  sub_name: string
  is_with_ri: boolean
  is_with_sl: boolean
  description: string
  is_with_toc: boolean
  origin_uuid: string
  playbook_type: string
  checklist_type: string
  origin_version: number
  is_with_checklist: boolean
  available_provisions: AvailableProvision[]
}

export interface ProvisionGuidance {
  provisionId: number
  provisionExternalId: number
  provisionName: string
  insightsAndLanguage: InsightsAndLanguage[]
}

export interface InsightsAndLanguage {
  label: string
  insights: string
  sampleLanguage: string
  insightsSource: 'default' | 'team'
  sampleLanguageSource: 'default' | 'team'
}

export interface ProjectSvcCrunches {
  id: number
  name: string
}

export interface TocDateSection {
  name: string
  show: boolean
  provisions: ProvisionSvcCrunches[]
}

export interface ProvisionSvcCrunches {
  ri: Ri
  sl: Sl
  fid: number
  name: string
  show: boolean
  position: number
  description: string
  name_original: string
}

export interface Ri {
  label: string
  description: string
}

export interface Sl {
  label: string
  description: string
}

export interface AvailableProvision {
  ri: Ri
  sl: Sl
  fid: number
  name: string
  show: boolean
  position: number
  description: string
  name_original: string
}

export interface ChecklistData {
  name: string
  type: string
  uuid: string
  version: number
  sections: ChecklistSection[]
  description: string
  origin_uuid: string
  origin_version: number
  is_side_by_side_ready: boolean
}

export interface ChecklistSection {
  name: string
  title: string
  ref_id: string
  metadata: Metadata
  position: number
  questions: ChecklistQuestion[]
  sub_heading: string
}

export interface Metadata {
  css: Css
}

export interface Css {
  class: string
  style: string
}

export interface ChecklistQuestion {
  id: number
  name: string
  type: string
  answer: Answer
  ref_id: string
  options: Options
  position: number
  sub_name: string
  type_name: string
  is_complex: boolean
  provisions: ProvisionSvcCrunches[]
  description: string
  is_required: boolean
  sub_questions: ChecklistQuestion[]
  has_notes_and_comments: boolean
  polices_and_preferences: string
}

export interface Answer {
  text: string
  option: string
  notes_and_comments: string
}

export interface Options {
  id: number
  values: Value[]
}

export interface Value {
  id: number
  name: string
}

export type JobStatus =
  | 'Pending'
  | 'Scheduled'
  | 'Running'
  | 'Succeeded'
  | 'Failed'
  | 'Cancelled'
  | 'Error'
  | 'Skipped'
  | 'Omitted'
  | 'Created'
export interface JobInfo {
  job_id: string
  app_name: string
  status: JobStatus
  root: boolean
}

export interface DocumentJobs {
  doc_guid: string
  jobs: ReadonlyArray<JobInfo>
}

export interface DocumentJobStatusResponse {
  data: ReadonlyArray<DocumentJobs>
}
